/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { ClipWithPlaylistTimes, PlaylistClip } from "types/Clip";

export function findClipIndicesAtTime(
  playlist: PlaylistClip[],
  time: number
): number[] {
  const indices: number[] = [];
  for (let clipsIndex = 0; clipsIndex < playlist.length; clipsIndex++) {
    const clip = playlist[clipsIndex];
    if (time >= clip.startTime && time < clip.endTime) {
      indices.push(clipsIndex);
    }
  }
  return indices;
}

export function findClipsAtTime(
  playlist: PlaylistClip[],
  time: number
): PlaylistClip[] {
  return findClipIndicesAtTime(playlist, time).map((index) => playlist[index]);
}

export function getPlaylistDuration(playlist: ClipWithPlaylistTimes[]): number {
  return playlist.length === 0
    ? 0
    : Math.max(...playlist.map((clip) => clip.endTime));
}

export const baseClipEqual = (
  oldClip: PlaylistClip,
  newClip: PlaylistClip
): boolean => {
  return (
    oldClip.id === newClip.id &&
    oldClip.offset === newClip.offset &&
    oldClip.duration === newClip.duration &&
    oldClip.assetId === newClip.assetId &&
    oldClip.type === newClip.type &&
    oldClip.startTime === newClip.startTime &&
    oldClip.endTime === newClip.endTime
  );
};

// TODO: Extract to utils and unit test.
/* Given a clip and a playlist time, find the offset within the audio file to start playback at.
   A playlist time may be given that is earlier than the start time of the clip, in which case the
   time to start playback within the audio file is equal to the offset (This will help with
    preloading) */
export const calculateElementTime = (
  currentPlaylistTime: number,
  clip: PlaylistClip
): number => {
  return (
    /*
                     {--element current time--}
        Asset:       [--clip.offset--|--------|-----------]
         Clip:                       [--------|-------]
     Playlist:  [--------------------|--------|-----------------]
                                     |        |
                              clip.startTime  |
                                              |
                                     currentPlaylistTime
       */
    Math.max(currentPlaylistTime - clip.startTime + clip.offset, clip.offset) /
    1000.0
  );
};
